import React from 'react';
import { useSelector } from 'react-redux';

import { useGetGroupsQuery, useGetUsersQuery } from '@/api/v1';
import { Group } from '@/DTOs/groups';
import { UpdateUserFormData } from '@/DTOs/users';
import { authSelector } from '@/StateManagement/AuthReducer';
import { User } from '@/types/user';

interface Props {
  readonly userId?: string;
}

interface UserFormProps {
  readonly loading?: boolean;
  readonly initialValues?: UpdateUserFormData;
  readonly key: React.Key;
  readonly isEditingSelf?: boolean;
}

export const EditUserContext: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactNode => {
  const { userId, children } = props;

  const { user: currentUser } = useSelector(authSelector);
  const { data: usersResponse, isLoading } = useGetUsersQuery({});
  const { data: groups = [] } = useGetGroupsQuery();

  if (!React.isValidElement<UserFormProps>(children)) {
    throw new Error('EditUserContext: child must be a valid element');
  }

  const properties = React.useMemo((): UserFormProps => {
    if (!usersResponse?.content || !userId) {
      return {
        loading: isLoading,
        key: 'loading',
        initialValues: undefined,
      };
    }

    const foundUser = usersResponse.content.find((user: User): boolean => user.id === userId);
    if (!foundUser) {
      return {
        loading: isLoading,
        key: 'loading',
        initialValues: undefined,
      };
    }

    // Map group name to group ID
    const getGroupIdFromName = (groupName: string): string => {
      // First, try to find by group name
      const groupByName = groups.find(
        (group: Group): boolean => group.groupName?.toLowerCase() === groupName?.toLowerCase(),
      );
      if (groupByName) {
        return groupByName.groupId;
      }

      // If not found by name, check if it's already a group ID
      const groupById = groups.find((group: Group): boolean => group.groupId === groupName);
      if (groupById) {
        return groupName;
      }

      // Fallback: return the first group ID if available, otherwise empty string
      return groups.length > 0 ? groups[0].groupId : '';
    };

    return {
      loading: isLoading,
      key: 'ready',
      isEditingSelf: currentUser?.id === userId,
      initialValues: {
        firstName: foundUser.firstName,
        lastName: foundUser.lastName,
        email: foundUser.email,
        department: foundUser.department?.toLowerCase() ?? 'engineering',
        group: getGroupIdFromName(foundUser.group || ''),
      },
    };
  }, [usersResponse?.content, userId, isLoading, groups, currentUser?.id]);

  return React.cloneElement(children, properties);
};
